import { useEffect, useMemo, useState } from 'react';
import { Card, ConfirmModal, Field, Skeleton } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { deleteBudget, fetchBudgets, saveBudget } from '../../firebase/repo';
import type { Budget, MasterType } from '../../types';
import { DROPDOWN_GROUPS, defaultDropdownValue, dropdownOptions } from '../../types/dropdowns';
import { formatMoney, monthKey, todayISO } from '../../utils/date';

export function BudgetManager({ uid, grouped }: { uid: string; grouped: Record<MasterType, string[]> }) {
  const toast = useToast();
  const [month, setMonth] = useState(monthKey(todayISO()));
  const categories = useMemo(() => dropdownOptions(grouped, DROPDOWN_GROUPS.DAILY_CATEGORY), [grouped]);
  const [draft, setDraft] = useState<Partial<Budget>>({ monthKey: month, category: categories[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY), limitAmount: 0, active: true, status: 'active' });
  const [formOpen, setFormOpen] = useState(false);
  const [rows, setRows] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Budget | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await fetchBudgets(uid, month));
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Budget load failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [uid, month]);
  useEffect(() => {
    setDraft((old) => ({ ...old, monthKey: month, category: old.category || categories[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY) }));
  }, [month, categories]);

  const duplicate = useMemo(() => rows.some((row) => row.id !== draft.id && row.category.toLowerCase() === String(draft.category || '').toLowerCase()), [draft, rows]);

  const resetDraft = () => setDraft({ monthKey: month, category: categories[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY), limitAmount: 0, active: true, status: 'active' });

  const startAdd = () => {
    resetDraft();
    setFormOpen(true);
  };

  const startEdit = (budget: Budget) => {
    setDraft(budget);
    setFormOpen(true);
  };

  const closeForm = () => {
    resetDraft();
    setFormOpen(false);
  };

  const save = async () => {
    if (saving) return;
    if (duplicate) return toast(`Budget already exists for ${draft.category} in ${month}`);
    setSaving(true);
    try {
      await saveBudget(uid, { ...draft, monthKey: month, active: true, status: 'active' });
      closeForm();
      await load();
      toast('Budget saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Budget save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget || saving) return;
    setSaving(true);
    try {
      await deleteBudget(uid, deleteTarget.id);
      setDeleteTarget(null);
      await load();
      toast('Budget deleted');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Budget delete failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return <Card className="settings-manager-card">
    <div className="section-title"><div><h2>Budget limits</h2><p className="muted">Monthly category limits for dashboard alerts.</p></div><button className="btn small primary" onClick={startAdd}>Create budget</button></div>
    <div className="settings-manager-toolbar">
      <Field label="Budget month"><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
      <span className="pill">{rows.length} budget{rows.length === 1 ? '' : 's'}</span>
    </div>
    {formOpen && <div className="settings-form-panel">
      <div className="section-title compact"><div><h3>{draft.id ? 'Edit budget' : 'New budget'}</h3><p className="muted">One active budget per category per month.</p></div><button className="btn small" onClick={closeForm}>Close</button></div>
      <div className="grid2">
        <Field label="Category"><select value={draft.category || categories[0]} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>{categories.map((category) => <option key={category}>{category}</option>)}</select></Field>
        <Field label="Limit amount"><input type="number" inputMode="decimal" value={draft.limitAmount || ''} onChange={(e) => setDraft({ ...draft, limitAmount: Number(e.target.value) })} placeholder="0" /></Field>
      </div>
      {duplicate && <p className="warning">This category already has a budget for {month}.</p>}
      <button className="btn primary wide" disabled={saving || duplicate || !Number(draft.limitAmount || 0)} onClick={save}>{saving ? 'Saving...' : draft.id ? 'Update budget' : 'Save budget'}</button>
    </div>}
    {loading ? <Skeleton /> : rows.map((row) => <div className="master-row" key={row.id}>
      <div><b>{row.category}</b><span>{row.monthKey} / limit {formatMoney(row.limitAmount)}</span></div>
      <div className="row-actions"><button className="btn small" onClick={() => startEdit(row)}>Edit</button><button className="btn small danger" onClick={() => setDeleteTarget(row)}>Del</button></div>
    </div>)}
    {!loading && !rows.length && <p className="empty-state">No budgets saved for this month.</p>}
    <ConfirmModal open={!!deleteTarget} title="Delete budget?" message="This soft deletes the budget limit. Past transactions remain unchanged." tone="danger" confirmLabel="Delete" loading={saving} details={deleteTarget ? [{ label: 'Category', value: deleteTarget.category }, { label: 'Month', value: deleteTarget.monthKey }, { label: 'Limit', value: formatMoney(deleteTarget.limitAmount) }] : []} onCancel={() => setDeleteTarget(null)} onConfirm={remove} />
  </Card>;
}
